import { Card, CardBody, CardHeader, Flex, Text } from '@backstage/ui';
import { EntityRefLink } from '@backstage/plugin-catalog-react';
import { MarkdownContent } from '@backstage/core-components';
import type {
  CheckDefinition,
  CheckRule,
  CheckType,
} from '@internal/backstage-plugin-pulse-common';
import { CheckRuleTree } from '../entity-content/check-rule-tree';
import {
  buildScope,
  describeFacets,
  parseRuleScope,
  type AnnotationFilter,
  type ScopeEntry,
  type ScopeFacetValues,
} from '../scope-filters/scope-filter-model';

export type CheckPreviewProps = {
  id: string;
  name: string;
  description: string;
  ownerEntityRef: string;
  includeScope: ScopeEntry[];
  excludeScope: ScopeEntry[];
  includeFacets?: ScopeFacetValues;
  excludeFacets?: ScopeFacetValues;
  includeAnnotations?: AnnotationFilter[];
  excludeAnnotations?: AnnotationFilter[];
  rule: CheckRule;
  passedMessage: string;
  failedMessage: string;
  type: CheckType;
};

const formatScopeEntry = ({ kind, types }: ScopeEntry): string =>
  types.length ? `${kind}: ${types.join(', ')}` : kind;

/** Maps a stored check definition onto the preview's presentational props. */
export const checkToPreviewProps = (
  check: CheckDefinition,
): CheckPreviewProps => {
  const include = parseRuleScope(check.filter);
  const exclude = parseRuleScope(check.exclude);

  return {
    id: check.id ?? '',
    name: check.name ?? '',
    description: check.description ?? '',
    ownerEntityRef: check.ownerEntityRef ?? '',
    includeScope: buildScope(include.kinds, include.typeKeys),
    excludeScope: buildScope(exclude.kinds, exclude.typeKeys),
    includeFacets: include.facets,
    excludeFacets: exclude.facets,
    includeAnnotations: include.annotations,
    excludeAnnotations: exclude.annotations,
    rule: check.rule ?? { all: [] },
    passedMessage: check.passedMessage ?? '',
    failedMessage: check.failedMessage ?? '',
    type: check.type,
  };
};

/** Read-only rendering of a check definition, without any surrounding chrome. */
export const CheckPreviewBody = ({
  id,
  name,
  description,
  ownerEntityRef,
  includeScope,
  excludeScope,
  includeFacets = {},
  excludeFacets = {},
  includeAnnotations = [],
  excludeAnnotations = [],
  rule,
  passedMessage,
  failedMessage,
  type,
}: CheckPreviewProps) => {
  const isManual = type === 'manual';
  const ruleItems = [...(rule.all ?? []), ...(rule.any ?? [])];
  const includeFacetLines = describeFacets(includeFacets, includeAnnotations);
  const excludeFacetLines = describeFacets(excludeFacets, excludeAnnotations);

  return (
    <Flex direction="column" gap="4">
      <Flex direction="column" gap="1">
        <Text variant="body-x-small" color="secondary">
          <code>{id || 'No ID'}</code>
        </Text>
        <Text variant="body-large" weight="bold">
          {name || 'Untitled check'}
        </Text>
        <Text variant="body-small" color="secondary">
          {description || 'No description'}
        </Text>
      </Flex>

      <Flex direction="column" gap="1">
        <Text variant="body-small" weight="bold">
          Owner
        </Text>
        {ownerEntityRef ? (
          <EntityRefLink entityRef={ownerEntityRef} underline="none" />
        ) : (
          <Text variant="body-small" color="secondary">
            No owner selected
          </Text>
        )}
      </Flex>

      <Flex direction="column" gap="1">
        <Text variant="body-small" weight="bold">
          Scope
        </Text>
        <Text variant="body-small" color="secondary">
          Includes:{' '}
          {includeScope.length
            ? includeScope.map(formatScopeEntry).join('; ')
            : 'any'}
        </Text>
        {includeFacetLines.map(line => (
          <Text
            key={line}
            variant="body-small"
            color="secondary"
            style={{ paddingLeft: 'var(--bui-space-3)' }}
          >
            {line}
          </Text>
        ))}
        <Text variant="body-small" color="secondary">
          Excludes:{' '}
          {excludeScope.length
            ? excludeScope.map(formatScopeEntry).join('; ')
            : 'none'}
        </Text>
        {excludeFacetLines.map(line => (
          <Text
            key={line}
            variant="body-small"
            color="secondary"
            style={{ paddingLeft: 'var(--bui-space-3)' }}
          >
            {line}
          </Text>
        ))}
      </Flex>

      <Flex direction="column" gap="1">
        <Text variant="body-small" weight="bold">
          Type
        </Text>
        <Text variant="body-small" color="secondary">
          {isManual ? 'Manual' : 'Automatic'}
        </Text>
      </Flex>

      {!isManual && (
        <>
          <Flex direction="column" gap="1">
            <Text variant="body-small" weight="bold">
              Rule
            </Text>
            {ruleItems.length === 0 ? (
              <Text variant="body-small" color="secondary">
                No conditions
              </Text>
            ) : (
              <CheckRuleTree rule={rule} />
            )}
          </Flex>

          <Flex direction="column" gap="1">
            <Text variant="body-small" weight="bold">
              Result messages
            </Text>
            <Flex direction="column" gap="0">
              <Text variant="body-small" color="secondary">
                Why this check passed
              </Text>
              <MarkdownContent content={passedMessage || 'No message'} />
            </Flex>
            <Flex direction="column" gap="0">
              <Text variant="body-small" color="secondary">
                Why this check failed and how to fix it
              </Text>
              <MarkdownContent content={failedMessage || 'No message'} />
            </Flex>
          </Flex>
        </>
      )}
    </Flex>
  );
};

/** Live read-only summary of the check currently being authored. */
export const CheckPreviewCard = (props: CheckPreviewProps) => (
  <Card>
    <CardHeader>
      <Text variant="title-x-small">Preview</Text>
    </CardHeader>
    <CardBody>
      <CheckPreviewBody {...props} />
    </CardBody>
  </Card>
);
